import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Col, Row } from 'antd';
import { Card, RichText, User, AtsSkeleton } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import './style.scss';

interface PersonRef {
  full_name?: string;
  avatar?: string | null;
}

interface ServiceRef {
  name?: string;
  service_head?: PersonRef;
}

interface CompanyRef {
  name?: string;
}

interface JobDescriptionTabProps {
  createdAt?: string;
  lastOpenedAt?: string | null;
  lastArchivedAt?: string | null;
  lastArchivedBy?: PersonRef | null;
  teamLead: PersonRef;
  resourceManager?: PersonRef | null;
  recruiters?: PersonRef[];
  recruiterLeads?: PersonRef[];
  service?: ServiceRef | null;
  company: CompanyRef;
  description?: string | null;
  benefits?: string | null;
}

export const JobDescriptionTab: React.FC<JobDescriptionTabProps> = ({
  createdAt,
  lastOpenedAt,
  lastArchivedAt,
  lastArchivedBy,
  teamLead,
  resourceManager,
  recruiters = [],
  recruiterLeads = [],
  service,
  company,
  description,
  benefits,
}) => {
  const { t } = useTranslation();
  return service ? (
    <Card className="job-description">
      <div className="job-description__general">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:job_description_tab.title_general')}
        </Typography.Title2>
        <Row className="margin-description" gutter={{ lg: 24, md: 24, sm: 16 }}>
          <Col lg={8} md={12} sm={24}>
            <Row>
              <Col span={24} className="margin-description__item">
                <Typography.Label className="gray-label">
                  {t('translation:job_description_tab.label_service')}
                </Typography.Label>
                <div>{service.name}</div>
              </Col>
              <Col span={24}>
                <Typography.Label className="gray-label">
                  {t('translation:job_description_tab.label_company')}
                </Typography.Label>
                <div>{company.name}</div>
              </Col>
            </Row>
          </Col>
          <Col lg={8} md={12} sm={24}>
            <Row>
              <Col span={24} className="margin-description__item">
                <Typography.Label className="gray-label">
                  {t('translation:job_description_tab.label_creation_date')}
                </Typography.Label>
                <div>{moment(createdAt).format('MMM DD, YYYY [at] HH:mm')}</div>
              </Col>
              <Col span={24}>
                <Typography.Label className="gray-label">
                  {t('translation:job_description_tab.label_opened_since')}
                </Typography.Label>
                <div>{lastOpenedAt ? moment(lastOpenedAt).format('MMM DD, YYYY [at] HH:mm') : '-'}</div>
              </Col>
            </Row>
          </Col>
          {(lastArchivedAt || lastArchivedBy) && (
            <Col lg={8} md={12} sm={24}>
              <Row>
                {lastArchivedAt && (
                  <Col span={24} className="margin-description__item">
                    <Typography.Label className="gray-label">
                      {t('translation:job_description_tab.label_last_archived_at')}
                    </Typography.Label>
                    <div>{moment(lastArchivedAt).format('MMM DD, YYYY [at] hh:mm')}</div>
                  </Col>
                )}
                {lastArchivedBy && (
                  <Col span={24}>
                    <Typography.Label className="gray-label">
                      {t('translation:job_description_tab.label_last_archived_by')}
                    </Typography.Label>
                    <User fullName={lastArchivedBy.full_name} avatar={lastArchivedBy.avatar} />
                  </Col>
                )}
              </Row>
            </Col>
          )}
        </Row>
        <div className="margin-description">
          <Typography.Label className="gray-label">
            {t('translation:job_description_tab.label_description')}
          </Typography.Label>
          <RichText text={description || '-'} />
        </div>
        <div className="benefits">
          <Typography.Label className="gray-label">
            {t('translation:job_description_tab.label_benefits')}
          </Typography.Label>
          <RichText text={benefits || '-'} />
        </div>
      </div>

      <div className="job-description__team">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:job_description_tab.title_team')}
        </Typography.Title2>
        <div className="margin-user">
          <Typography.Label>{t('translation:job_description_tab.label_team_lead')}</Typography.Label>
          <User fullName={teamLead.full_name} avatar={teamLead.avatar} />
        </div>
        <Row className="job-description__rm-sh margin-user" gutter={{ lg: 24, md: 24, sm: 16 }}>
          <Col lg={8} md={12} sm={24}>
            <Typography.Label>{t('translation:job_description_tab.label_resource_manager')}</Typography.Label>
            <User fullName={resourceManager?.full_name} avatar={resourceManager?.avatar} />
          </Col>
          {service.service_head && (
            <Col lg={8} md={12} sm={24}>
              <Typography.Label>{t('translation:job_description_tab.label_service_head')}</Typography.Label>
              <User fullName={service.service_head.full_name} avatar={service.service_head.avatar} />
            </Col>
          )}
        </Row>
        <Typography.Label>{t('translation:job_description_tab.label_recruiter_leads')}</Typography.Label>
        <Row className="job-description__recruiters margin-user" gutter={{ lg: 24, md: 24, sm: 16 }}>
          {recruiterLeads.length ? (
            recruiterLeads.map((r, index) => (
              <Col key={index} lg={8} md={12} sm={24}>
                <User fullName={r.full_name} avatar={r.avatar} />
              </Col>
            ))
          ) : (
            <Col span={24}>
              <div className="empty-recruiters">
                {t('translation:job_description_tab.text_recruiter_leads_not_assigned')}
              </div>
            </Col>
          )}
        </Row>
        <Typography.Label>{t('translation:job_description_tab.label_recruiters')}</Typography.Label>
        <Row className="job-description__recruiters" gutter={{ lg: 24, md: 24, sm: 16 }}>
          {recruiters.length ? (
            recruiters.map((r, index) => (
              <Col key={index} lg={8} md={12} sm={24}>
                <User fullName={r.full_name} avatar={r.avatar} />
              </Col>
            ))
          ) : (
            <Col span={24}>
              <div className="empty-recruiters">
                {t('translation:job_description_tab.text_recruiters_not_assigned')}
              </div>
            </Col>
          )}
        </Row>
      </div>
    </Card>
  ) : (
    <Card className="job-description">
      <AtsSkeleton height={400} />
    </Card>
  );
};
